import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  BarChart3,
  Calculator,
  CheckCircle,
  FileText,
  Mail,
  Maximize,
  Minimize,
  Users,
  X,
} from 'lucide-react'
import { z } from 'zod'

import { RmdSessionsList } from './-components/RmdSessionsList'
import { RmdMyClientsCard } from './-components/RmdMyClientsCard'
import { RmdPersonalInfoCard } from './-components/RmdPersonalInfoCard'
import { RmdFinancialDetailsCard } from './-components/RmdFinancialDetailsCard'
import { RmdReviewCard } from './-components/RmdReviewCard'
import { RmdSummaryCards } from './-components/RmdSummaryCards'
import { RmdFirstYearSection } from './-components/RmdFirstYearSection'
import { RmdTaxImpactCard } from './-components/RmdTaxImpactCard'
import { RmdLifetimeProjections } from './-components/RmdLifetimeProjections'
import { RmdHeirsImpactCard } from './-components/RmdHeirsImpactCard'
import { RmdWidowsTrapCard } from './-components/RmdWidowsTrapCard'
import { RmdProjectionsTable } from './-components/RmdProjectionsTable'
import { RmdHeirsImpactTable } from './-components/RmdHeirsImpactTable'
import { RmdLoadingSkeleton } from './-components/RmdLoadingSkeleton'
import { RmdErrorState } from './-components/RmdErrorState'
import { RmdPdfViewerModal } from './-components/RmdPdfViewerModal'
import { RmdEmailModal } from './-components/RmdEmailModal'
import type { ClientSummary } from './-components/RmdMyClientsCard'
import type {
  RmdCalculateAndSaveInput,
  RmdCalculationResult,
  RmdCalculationStatus,
} from '@/types/rmd.types'
import type { LucideIcon } from 'lucide-react'
import { queryKeys } from '@/lib/query-client'
import {
  useCalculateRmd,
  useGenerateRmdPdf,
  useHubspotUploadStatus,
  useRmdJobStatus,
  useRmdSession,
  useRmdWithPdf,
  useTouchRmdSession,
} from '@/hooks/repositories/useRmdRepository'
import { useRmdSessionPersistence } from '@/hooks/use-cases/useRmdSessionPersistence'
import { useFindClientByEmail } from '@/hooks/repositories/useClientsRepository'
import {
  RmdWizardStep,
  TaxFilingStatus,
  getAgeFromBirthYear,
  getRmdStartAge,
  isBasicCalculationValid,
} from '@/types/rmd.types'
import { Separator } from '@/components/ui/separator'
import { Progress } from '@/components/ui/progress'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { PageTitle } from '@/components/PageTitle'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/animate-ui/components/radix/sheet'

const RESULT_TABS: Array<{ value: string; label: string; icon: LucideIcon }> = [
  { value: 'summary', label: 'Summary', icon: Calculator },
  { value: 'projections', label: 'Projections', icon: BarChart3 },
  { value: 'heirs', label: 'Heirs', icon: Users },
]

const rmdCalculatorSearchSchema = z.object({
  sessionId: z.string().optional(),
})

const DEFAULT_RMD_INPUTS: RmdCalculateAndSaveInput = {
  dateOfBirth: '1955',
  expectedReturn: 5.5,
  hasSpouse: false,
  accountBalance: 1200000,
  taxableIncome: 100000,
  filingStatus: TaxFilingStatus.SINGLE,
  firstName: '',
  lastName: '',
  email: '',
}

export const Route = createFileRoute('/_app/tools/rmd-calculator/')({
  validateSearch: rmdCalculatorSearchSchema,
  component: RmdCalculatorPage,
})

function RmdCalculatorPage() {
  const { sessionId } = Route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // ─── Form State ────────────────────────────────────────────────────────────
  const [inputs, setInputs] =
    useState<RmdCalculateAndSaveInput>(DEFAULT_RMD_INPUTS)

  // ─── UI State ──────────────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState<RmdWizardStep>(
    RmdWizardStep.PERSONAL_INFO,
  )
  const [isCalculating, setIsCalculating] = useState(false)
  const [results, setResults] = useState<RmdCalculationResult | null>(null)
  const [showResults, setShowResults] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [activeTab, setActiveTab] = useState('summary')
  const [error, setError] = useState<string | null>(null)

  // ─── Session Persistence State ────────────────────────────────────────────
  const [calculationStatus, setCalculationStatus] =
    useState<RmdCalculationStatus | null>(null)
  const hydratedSessionIdRef = useRef<string | null>(null)

  // ─── PDF State ─────────────────────────────────────────────────────────────
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [calculationId, setCalculationId] = useState<string | null>(null)

  // ─── Email State ───────────────────────────────────────────────────────────
  const [showEmailModal, setShowEmailModal] = useState(false)

  // ─── Client Filter State ───────────────────────────────────────────────────
  const [filterClient, setFilterClient] = useState<ClientSummary | null>(null)

  // ─── Sessions Drawer State ─────────────────────────────────────────────────
  const [sessionsOpen, setSessionsOpen] = useState(false)

  // ─── Client Auto-fill State ───────────────────────────────────────────────
  const [isSearchingClient, setIsSearchingClient] = useState(false)
  const [autoFilledClientName, setAutoFilledClientName] = useState<
    string | null
  >(null)
  const [prevNamesBeforeAutoFill, setPrevNamesBeforeAutoFill] = useState<{
    firstName: string
    lastName: string
  } | null>(null)

  // ─── Repository Hooks ──────────────────────────────────────────────────────
  const calculateRmdMutation = useCalculateRmd()
  const touchSessionMutation = useTouchRmdSession()
  const findClientByEmailQuery = useFindClientByEmail(inputs.email ?? '')
  const rmdWithPdfQuery = useRmdWithPdf(calculationId)

  // ─── Session Resume ────────────────────────────────────────────────────────
  const sessionQuery = useRmdSession(sessionId ?? null)

  // ─── Session Persistence Use-Case ─────────────────────────────────────────
  // Declared before the hydration effect so markHydrated is available to call
  // inside that effect, preventing a spurious auto-save on the first tick.
  const { saveProgress, flushOnBlur, markHydrated } = useRmdSessionPersistence({
    inputs,
    calculationId,
    calculationStatus,
    currentStep,
    onDraftCreated: (id) => {
      setCalculationId(id)
      setCalculationStatus('DRAFT')
    },
    onSessionRevertedToDraft: () => {
      setCalculationStatus('DRAFT')
      setResults(null)
      setShowResults(false)
      setPdfUrl(null)
      setPdfJobId(null)
      // Close the PDF modal too — without this it can linger with a blank
      // viewer if a step-transition save reverts a COMPLETED session while
      // the user has the modal open (sc-313 review).
      setShowPdfModal(false)
      pdfJobCompletedRef.current = false
      toast.info('Inputs changed — recalculate to update results.')
    },
  })

  useEffect(() => {
    if (!sessionQuery.data) return
    if (hydratedSessionIdRef.current === sessionQuery.data.id) return

    const session = sessionQuery.data
    hydratedSessionIdRef.current = session.id

    // Hydrate form inputs. Strip stray `draftId` and `lastStepCompleted` left
    // over from older payloads — neither is a valid CalculateRmdDto field, and
    // both will fail validation when echoed back to calculate or auto-save.
    const {
      draftId: _draftId,
      lastStepCompleted: _lastStep,
      ...sessionInputs
    } = session.inputs as typeof session.inputs & {
      draftId?: string
      lastStepCompleted?: number
    }

    const merged = { ...inputs, ...sessionInputs }
    setInputs(merged)

    setCalculationId(session.id)
    setCalculationStatus(session.status ?? null)

    let hydratedStep: RmdWizardStep
    if (session.status === 'COMPLETED') {
      // Restore completed view
      if (session.results) {
        setResults(session.results)
        setShowResults(true)
      }
      hydratedStep = RmdWizardStep.REVIEW
      setCurrentStep(hydratedStep)
      if (session.pdfUrl) setPdfUrl(session.pdfUrl)
    } else {
      // Restore wizard step for draft
      hydratedStep = Math.min(
        session.lastStepCompleted ?? 0,
        RmdWizardStep.REVIEW,
      ) as RmdWizardStep
      setCurrentStep(hydratedStep)
    }

    // Bump updatedAt so the opened session floats to the top of recent sessions
    touchSessionMutation.mutate(session.id)

    // Seed the last-saved snapshot so the debounced auto-save does not treat
    // the just-hydrated state as a change and spuriously PATCH the server
    // (which would revert a COMPLETED session to DRAFT).
    markHydrated({ ...merged, lastStepCompleted: hydratedStep })
  }, [sessionQuery.data])

  // ─── PDF Generation Hooks ──────────────────────────────────────────────────
  const generateRmdPdfMutation = useGenerateRmdPdf(calculationId ?? '')
  const hubspotStatus = useHubspotUploadStatus(calculationId)

  const [pdfJobId, setPdfJobId] = useState<string | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  const pdfJobStatusQuery = useRmdJobStatus(calculationId ?? '', pdfJobId, {
    enabled: !!pdfJobId && !!calculationId,
  })

  // React to PDF job completion — get the URL
  const pdfJobState = pdfJobStatusQuery.data?.state
  const pdfJobCompletedRef = useRef(false)

  useEffect(() => {
    if (
      pdfJobState !== 'completed' ||
      !calculationId ||
      pdfJobCompletedRef.current
    )
      return
    pdfJobCompletedRef.current = true
    setPdfLoading(true)

    // Always fetch the signed URL via the /with-pdf endpoint.
    // The job status returns a raw S3 URL that won't work in the browser (CORS).
    // The /with-pdf endpoint returns a signed URL that works in an iframe.
    rmdWithPdfQuery
      .refetch()
      .then((result) => {
        const response = result.data
        if (response?.pdfUrl) {
          setPdfUrl(response.pdfUrl)
          hubspotStatus.refetch()
        } else {
          // Fallback: use the raw URL from job data (will work in iframe for most S3 configs)
          const jobPdfUrl = pdfJobStatusQuery.data?.data?.pdfUrl
          if (jobPdfUrl) setPdfUrl(jobPdfUrl)
        }
      })
      .catch(() => {
        // Last resort: use the raw URL from job data
        const jobPdfUrl = pdfJobStatusQuery.data?.data?.pdfUrl
        if (jobPdfUrl) setPdfUrl(jobPdfUrl)
      })
      .finally(() => setPdfLoading(false))
  }, [pdfJobState, calculationId])

  // Reset the completion ref when jobId is cleared
  useEffect(() => {
    if (!pdfJobId) {
      pdfJobCompletedRef.current = false
    }
  }, [pdfJobId])

  // React to PDF job failure — surface the error, stop the spinner, close the
  // modal. Without this the modal stays at "Generating Report... 20%" forever
  // because pdfLoading is only reset by the completed branch (sc-313).
  useEffect(() => {
    if (pdfJobState !== 'failed') return
    setPdfLoading(false)
    setPdfJobId(null)
    setShowPdfModal(false)
    toast.error('Could not generate PDF. Please try again.')
  }, [pdfJobState])

  // ─── Computed Values ───────────────────────────────────────────────────────
  const inSession = !!sessionId || !!calculationId

  // ─── Auto-close sessions drawer when a session becomes active ─────────────
  useEffect(() => {
    if (inSession) setSessionsOpen(false)
  }, [inSession])

  const currentAge = useMemo(
    () => (inputs.dateOfBirth ? getAgeFromBirthYear(inputs.dateOfBirth) : null),
    [inputs.dateOfBirth],
  )

  const rmdInfo = useMemo(() => {
    if (!currentAge || !inputs.dateOfBirth) return null
    const rmdStartAge = getRmdStartAge(inputs.dateOfBirth)
    const yearsUntilRmd = Math.max(0, rmdStartAge - currentAge)
    const rmdStartYear = parseInt(inputs.dateOfBirth) + rmdStartAge
    const alreadyStarted = currentAge > rmdStartAge
    return { rmdStartAge, rmdStartYear, yearsUntilRmd, alreadyStarted }
  }, [currentAge, inputs.dateOfBirth])

  const isFormValid = useMemo(() => isBasicCalculationValid(inputs), [inputs])

  // PDF progress derived from job status
  const pdfProgress = pdfJobStatusQuery.data?.progress ?? 0
  // Show generating state while: job is in progress, OR job completed but URL is still loading
  const isPdfGenerating =
    (!!pdfJobId &&
      pdfJobStatusQuery.data?.state !== 'completed' &&
      pdfJobStatusQuery.data?.state !== 'failed') ||
    pdfLoading

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const updateInput = useCallback(
    (
      field: keyof RmdCalculateAndSaveInput,
      value: string | number | boolean,
    ) => {
      setInputs((prev) => {
        const updated = { ...prev, [field]: value }
        if (field === 'hasSpouse' && value === false) {
          delete updated.spouseDateOfBirth
        }
        if (
          field === 'hasSpouse' &&
          value === true &&
          !updated.spouseDateOfBirth
        ) {
          updated.spouseDateOfBirth = ''
        }
        return updated
      })
    },
    [],
  )

  // Debounced email client lookup
  const emailRef = useRef(inputs.email)
  emailRef.current = inputs.email

  useEffect(() => {
    const email = inputs.email?.trim() ?? ''
    const isValidEmail =
      email.length >= 5 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

    setAutoFilledClientName(null)
    if (!isValidEmail) return

    const searchTimer = setTimeout(async () => {
      setIsSearchingClient(true)
      const loadingTimeout = setTimeout(() => setIsSearchingClient(false), 5000)

      try {
        const result = await findClientByEmailQuery.refetch()
        clearTimeout(loadingTimeout)

        const client = result.data
        if (client) {
          let capturedFirstName = ''
          let capturedLastName = ''

          setInputs((prev) => {
            capturedFirstName = prev.firstName ?? ''
            capturedLastName = prev.lastName ?? ''
            return {
              ...prev,
              firstName: client.firstName,
              lastName: client.lastName,
            }
          })

          setPrevNamesBeforeAutoFill({
            firstName: capturedFirstName,
            lastName: capturedLastName,
          })
          setAutoFilledClientName(
            [client.firstName, client.lastName].filter(Boolean).join(' '),
          )
        }
      } catch {
        clearTimeout(loadingTimeout)
      } finally {
        setIsSearchingClient(false)
      }
    }, 800)

    return () => clearTimeout(searchTimer)
  }, [inputs.email])

  const handleUndoAutoFill = useCallback(() => {
    if (prevNamesBeforeAutoFill) {
      setInputs((prev) => ({
        ...prev,
        firstName: prevNamesBeforeAutoFill.firstName,
        lastName: prevNamesBeforeAutoFill.lastName,
      }))
      setPrevNamesBeforeAutoFill(null)
    }
    setAutoFilledClientName(null)
  }, [prevNamesBeforeAutoFill])

  const handleCalculate = async () => {
    if (!isBasicCalculationValid(inputs)) return

    setIsCalculating(true)
    setError(null)

    try {
      const payload = {
        ...inputs,
        widowsTrapEnabled: Boolean(inputs.widowsTrapEnabled),
        ...(calculationId ? { draftId: calculationId } : {}),
      }
      const response = await calculateRmdMutation.mutateAsync(payload)
      setResults(response.results)
      setCalculationId(response.id)
      setCalculationStatus('COMPLETED')
      if (response.pdfUrl) {
        setPdfUrl(response.pdfUrl)
      }
      setShowResults(true)
    } catch {
      setError('Network error. Please check your connection and try again.')
    } finally {
      setIsCalculating(false)
    }
  }

  const resetFormState = useCallback(() => {
    setInputs(DEFAULT_RMD_INPUTS)
    setResults(null)
    setShowResults(false)
    setIsCalculating(false)
    setCurrentStep(RmdWizardStep.PERSONAL_INFO)
    setIsFullscreen(false)
    setError(null)
    setCalculationId(null)
    setCalculationStatus(null)
    setPdfUrl(null)
    setPdfJobId(null)
    setShowPdfModal(false)
    setAutoFilledClientName(null)
    setPrevNamesBeforeAutoFill(null)
    pdfJobCompletedRef.current = false
    hydratedSessionIdRef.current = null
  }, [])

  const handleReset = () => {
    resetFormState()
    void queryClient.invalidateQueries({ queryKey: queryKeys.rmd.sessions() })
    void queryClient.invalidateQueries({ queryKey: queryKeys.clients.all })
    void navigate({ to: '/tools/rmd-calculator', search: {} })
  }

  const prevSessionIdRef = useRef<string | undefined>(sessionId)

  useEffect(() => {
    const prev = prevSessionIdRef.current
    prevSessionIdRef.current = sessionId
    if (prev && !sessionId) {
      resetFormState()
      void queryClient.invalidateQueries({ queryKey: queryKeys.rmd.sessions() })
    }
  }, [sessionId, resetFormState, queryClient])

  const handleGeneratePdf = async () => {
    if (!calculationId) return
    setShowPdfModal(true)

    // Check if PDF already exists (signed URL)
    try {
      const result = await rmdWithPdfQuery.refetch()
      if (result.data?.pdfUrl) {
        setPdfUrl(result.data.pdfUrl)
        return
      }
    } catch {
      // No existing PDF — proceed to generate
    }

    // No existing PDF — generate a new one
    try {
      pdfJobCompletedRef.current = false
      setPdfLoading(true)
      const result = await generateRmdPdfMutation.mutateAsync('final')
      if (result.jobId) {
        setPdfJobId(String(result.jobId))
      } else {
        setPdfLoading(false)
      }
    } catch {
      setPdfLoading(false)
      setShowPdfModal(false)
    }
  }

  const closePdfModal = () => {
    setShowPdfModal(false)
    setPdfJobId(null)
  }

  // ─── Wizard Navigation ─────────────────────────────────────────────────────
  const nextStep = () => {
    const next = Math.min(
      currentStep + 1,
      RmdWizardStep.REVIEW,
    ) as RmdWizardStep
    // Save draft progress when advancing (fire-and-forget — non-blocking)
    void saveProgress(next)
    setCurrentStep(next)
  }
  const prevStep = () =>
    setCurrentStep(
      (prev) =>
        Math.max(prev - 1, RmdWizardStep.PERSONAL_INFO) as RmdWizardStep,
    )
  const goToStep = (step: RmdWizardStep) => setCurrentStep(step)

  const progressPercent = ((currentStep + 1) / 3) * 100

  return (
    <div
      className={
        isFullscreen
          ? 'fixed inset-0 z-50 bg-background overflow-y-auto'
          : 'flex flex-col min-h-0'
      }
    >
      <PageTitle title="RMD Calculator" />
      {/* placeholder — header removed, actions moved inline with tabs */}

      {/* Main Layout */}
      <div
        className={`flex gap-4 p-4 ${isFullscreen ? 'flex-col' : 'flex-col lg:flex-row'}`}
      >
        {/* Wizard Panel */}
        {!isFullscreen && (
          <div
            className="w-full self-start lg:w-96 flex-shrink-0"
            onBlur={flushOnBlur}
          >
            {inSession && (
              <Button
                variant="ghost"
                size="sm"
                className="mb-2 gap-1.5 text-xs"
                onClick={handleReset}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to RMD Calculator
              </Button>
            )}
            <AnimatePresence mode="wait">
              {currentStep === RmdWizardStep.PERSONAL_INFO && (
                <motion.div
                  key="personal-info"
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 24 }}
                  transition={{ duration: 0.25 }}
                >
                  <RmdPersonalInfoCard
                    inputs={inputs}
                    updateInput={updateInput}
                    currentAge={currentAge}
                    rmdInfo={rmdInfo}
                    onNext={nextStep}
                    isSearchingClient={isSearchingClient}
                    autoFilledClientName={autoFilledClientName}
                    onUndoAutoFill={handleUndoAutoFill}
                  />
                </motion.div>
              )}
              {currentStep === RmdWizardStep.FINANCIAL_DETAILS && (
                <motion.div
                  key="financial-details"
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 24 }}
                  transition={{ duration: 0.25 }}
                >
                  <RmdFinancialDetailsCard
                    inputs={inputs}
                    updateInput={updateInput}
                    onPrevious={prevStep}
                    onNext={nextStep}
                  />
                </motion.div>
              )}
              {currentStep === RmdWizardStep.REVIEW && (
                <motion.div
                  key="review"
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 24 }}
                  transition={{ duration: 0.25 }}
                >
                  <RmdReviewCard
                    inputs={inputs}
                    currentAge={currentAge}
                    rmdInfo={rmdInfo}
                    isValid={isFormValid}
                    onPrevious={prevStep}
                    onCalculate={handleCalculate}
                    onEditPersonal={() => goToStep(RmdWizardStep.PERSONAL_INFO)}
                    onEditFinancial={() =>
                      goToStep(RmdWizardStep.FINANCIAL_DETAILS)
                    }
                    onReset={handleReset}
                    showResults={showResults}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 min-w-0">
          <AnimatePresence mode="wait">
            {/* Error State */}
            {error && (
              <motion.div
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <RmdErrorState
                  error={error}
                  onRetry={() => setError(null)}
                  onStartOver={handleReset}
                />
              </motion.div>
            )}

            {/* Loading State */}
            {!error && isCalculating && (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <RmdLoadingSkeleton />
              </motion.div>
            )}

            {/* Pre-calculation Hero */}
            {!error && !isCalculating && !showResults && (
              <motion.div
                key="waiting"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.3 }}
                className="space-y-4"
              >
                {/* Resume Banner */}
                {sessionId && sessionQuery.data && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5"
                  >
                    <p className="text-sm text-primary font-medium">
                      Resuming session
                      {inputs.firstName
                        ? ` for ${inputs.firstName}${inputs.lastName ? ` ${inputs.lastName}` : ''}`
                        : ''}
                    </p>
                    <button
                      type="button"
                      aria-label="Clear session"
                      onClick={handleReset}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </motion.div>
                )}

                {/* Hero */}
                <Sheet open={sessionsOpen} onOpenChange={setSessionsOpen}>
                  <div className="bg-primary/5 rounded-xl p-5 border border-primary/20">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center flex-shrink-0 shadow-sm">
                          <Calculator className="h-6 w-6 text-primary-foreground" />
                        </div>
                        <div>
                          <h1 className="text-xl font-bold mb-1">
                            RMD Calculator with Tax Impact
                          </h1>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            Calculate your Required Minimum Distributions and
                            understand their tax implications. Essential for
                            retirees with tax-deferred retirement accounts.
                          </p>
                        </div>
                      </div>
                      {!inSession && (
                        <SheetTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-shrink-0 gap-1.5"
                          >
                            <Users className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">
                              View Sessions
                            </span>
                          </Button>
                        </SheetTrigger>
                      )}
                    </div>
                  </div>
                  <SheetContent
                    side="right"
                    className="w-full sm:max-w-md md:max-w-lg overflow-y-auto"
                  >
                    <SheetHeader>
                      <SheetTitle>Sessions</SheetTitle>
                      <SheetDescription>
                        Filter by client or resume a recent RMD calculation.
                      </SheetDescription>
                    </SheetHeader>
                    <div className="space-y-4 p-4">
                      <RmdSessionsList
                        clientFilter={filterClient}
                        onClearClientFilter={() => setFilterClient(null)}
                      />
                      <RmdMyClientsCard
                        selectedClient={filterClient}
                        onSelect={setFilterClient}
                      />
                    </div>
                  </SheetContent>
                </Sheet>

                {/* Info Cards */}
                <Card className="border-primary/20">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Why RMDs Matter */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-destructive" />
                          <p className="text-xs font-semibold">
                            Why RMDs Matter
                          </p>
                        </div>
                        <div className="space-y-2 text-xs text-muted-foreground">
                          <div className="flex items-start gap-2">
                            <div className="w-1 h-1 rounded-full bg-destructive mt-1.5 flex-shrink-0" />
                            <p>
                              <strong className="text-foreground">
                                Required by Law:
                              </strong>{' '}
                              IRS mandates minimum withdrawals from tax-deferred
                              accounts starting at age 73 or 75
                            </p>
                          </div>
                          <div className="flex items-start gap-2">
                            <div className="w-1 h-1 rounded-full bg-destructive mt-1.5 flex-shrink-0" />
                            <p>
                              <strong className="text-foreground">
                                Heavy Penalties:
                              </strong>{' '}
                              50% penalty on missed RMD amounts
                            </p>
                          </div>
                          <div className="flex items-start gap-2">
                            <div className="w-1 h-1 rounded-full bg-destructive mt-1.5 flex-shrink-0" />
                            <p>
                              <strong className="text-foreground">
                                Tax Impact:
                              </strong>{' '}
                              RMDs are taxed as ordinary income
                            </p>
                          </div>
                        </div>
                      </div>

                      <Separator className="lg:hidden" />

                      {/* What You'll Get */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-3.5 h-3.5 text-primary" />
                          <p className="text-xs font-semibold">
                            What You&apos;ll Get
                          </p>
                        </div>
                        <div className="space-y-2 text-xs text-muted-foreground">
                          <div className="flex items-start gap-2">
                            <div className="w-1 h-1 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                            <p>
                              <strong className="text-foreground">
                                Precise Calculations:
                              </strong>{' '}
                              IRS-approved RMD amounts for each year
                            </p>
                          </div>
                          <div className="flex items-start gap-2">
                            <div className="w-1 h-1 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                            <p>
                              <strong className="text-foreground">
                                Tax Projections:
                              </strong>{' '}
                              Estimated tax impact and strategies
                            </p>
                          </div>
                          <div className="flex items-start gap-2">
                            <div className="w-1 h-1 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                            <p>
                              <strong className="text-foreground">
                                Actionable Insights:
                              </strong>{' '}
                              Personalized recommendations
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Progress Indicator */}
                <Card className="border-primary/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold">Your Progress</p>
                      <span className="text-xs text-muted-foreground">
                        Step {currentStep + 1} of 3
                      </span>
                    </div>
                    <Progress value={progressPercent} className="h-2" />
                    <div className="flex justify-between mt-2">
                      {['Personal Info', 'Financial Details', 'Review'].map(
                        (label, idx) => (
                          <span
                            key={label}
                            className={`text-[10px] font-medium ${
                              idx <= currentStep
                                ? 'text-primary'
                                : 'text-muted-foreground'
                            }`}
                          >
                            {label}
                          </span>
                        ),
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* About RMDs Section */}
                <Card className="border-muted">
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold mb-3">About RMDs</h3>
                    <div className="space-y-2 text-xs text-muted-foreground leading-relaxed">
                      <p>
                        Required Minimum Distributions (RMDs) are the minimum
                        amounts you must withdraw annually from traditional
                        IRAs, 401(k)s, and other tax-deferred retirement
                        accounts once you reach the required beginning date.
                      </p>
                      <p>
                        The SECURE 2.0 Act changed RMD start ages. If you were
                        born in 1951–1959, RMDs begin at age 73. If born in 1960
                        or later, RMDs begin at age 75.
                      </p>
                      <p>
                        RMD amounts are calculated by dividing your account
                        balance by a life expectancy factor from IRS tables.
                        This calculator uses the Uniform Lifetime Table (or
                        Joint Life Table if your spouse is the sole beneficiary
                        and more than 10 years younger).
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Results */}
            {!error && !isCalculating && showResults && results && (
              <motion.div
                key="results"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="space-y-4"
              >
                {/* Tabs + Actions row */}
                <div className="flex items-center justify-between gap-3">
                  {/* Animated tab bar */}
                  <div className="relative flex gap-1 rounded-lg bg-muted p-1">
                    {RESULT_TABS.map((tab) => (
                      <button
                        key={tab.value}
                        onClick={() => setActiveTab(tab.value)}
                        className={`relative z-10 flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                          activeTab === tab.value
                            ? 'text-foreground'
                            : 'text-muted-foreground hover:text-foreground/80'
                        }`}
                      >
                        <tab.icon className="h-3.5 w-3.5" />
                        {tab.label}
                        {activeTab === tab.value && (
                          <motion.div
                            layoutId="rmd-tab-indicator"
                            className="absolute inset-0 rounded-md bg-background shadow-sm"
                            style={{ zIndex: -1 }}
                            transition={{
                              type: 'spring',
                              stiffness: 350,
                              damping: 30,
                            }}
                          />
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1.5"
                      onClick={handleGeneratePdf}
                      disabled={!calculationId}
                    >
                      <FileText className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">PDF</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1.5"
                      onClick={() => setShowEmailModal(true)}
                      disabled={!calculationId}
                    >
                      <Mail className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Email</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setIsFullscreen(!isFullscreen)}
                    >
                      {isFullscreen ? (
                        <Minimize className="h-3.5 w-3.5" />
                      ) : (
                        <Maximize className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Tab content with animated transitions */}
                <AnimatePresence mode="wait">
                  {activeTab === 'summary' && (
                    <motion.div
                      key="tab-summary"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-4"
                    >
                      <RmdSummaryCards results={results} />
                      <RmdFirstYearSection results={results} inputs={inputs} />
                      <RmdTaxImpactCard results={results} inputs={inputs} />
                      <RmdLifetimeProjections results={results} />
                      <RmdHeirsImpactCard results={results} />
                      <RmdWidowsTrapCard results={results} />
                    </motion.div>
                  )}

                  {activeTab === 'projections' && (
                    <motion.div
                      key="tab-projections"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                    >
                      <RmdProjectionsTable results={results} />
                    </motion.div>
                  )}

                  {activeTab === 'heirs' && (
                    <motion.div
                      key="tab-heirs"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                    >
                      <RmdHeirsImpactTable results={results} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Modals */}
      <RmdPdfViewerModal
        isOpen={showPdfModal}
        onClose={closePdfModal}
        pdfUrl={pdfUrl}
        isGenerating={isPdfGenerating}
        progress={pdfProgress}
        calculationId={calculationId ?? ''}
        clientName={
          inputs.firstName
            ? `${inputs.firstName} ${inputs.lastName ?? ''}`.trim()
            : undefined
        }
        hubspotUploadStatus={hubspotStatus.data?.status}
      />

      <RmdEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        calculationId={calculationId ?? ''}
        prefilledEmail={inputs.email}
        prefilledName={
          inputs.firstName
            ? `${inputs.firstName} ${inputs.lastName ?? ''}`.trim()
            : undefined
        }
        calculationSummary={{
          rmdStartAge: results?.rmdStartAge ?? 73,
          rmdStartYear: results?.rmdStartYear ?? new Date().getFullYear() + 10,
          firstYearRmdAmount: results?.firstYearRmd.rmdAmount ?? 0,
          totalTaxesPaid: results?.summary.totalTaxesPaid ?? 0,
          rmdAlreadyStarted: results?.rmdAlreadyStarted ?? false,
          firstYearRmdYear: results?.firstYearRmd.year,
        }}
        onEmailSent={() => {
          // Email sent callback - could trigger history refresh
        }}
      />
    </div>
  )
}
